import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getBalanceSheetComparison } from "./balanceSheetComparisonService";
import BalanceSheetComparisonTable from "./BalanceSheetComparisonTable";
import { FaCalendarAlt, FaFileInvoiceDollar, FaChartLine, FaArrowLeft } from "react-icons/fa";

const BalanceSheetComparisonPage = () => {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numYears, setNumYears] = useState(5);

  useEffect(() => {
    fetchReport(numYears);
  }, []);

  const fetchReport = async (n) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBalanceSheetComparison(n);
      setReportData(data);
    } catch (err) {
      setError("Failed to load Balance Sheet comparison report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleYearsChange = (n) => {
    setNumYears(n);
    fetchReport(n);
  };

  return (
    <div className="container-fluid py-4 px-3 px-md-4">
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 bg-primary-subtle text-primary rounded-3">
              <FaBalanceScaleHeader />
            </div>
            <div>
              <h3 className="mb-0 fw-bold text-dark">Balance Sheet Comparison</h3>
              <small className="text-muted">
                Side-by-side annual comparison of Assets, Liabilities, and Equity (up to 5 years)
              </small>
            </div>
          </div>
        </div>

        {/* Quick Navigations */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate("/dashboard/reports/balance-sheet")}
          >
            <FaFileInvoiceDollar /> Standard Balance Sheet
          </button>
          <button
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate("/dashboard/reports/pnl-comparison")}
          >
            <FaChartLine /> P&amp;L Comparison
          </button>
          <button
            className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Filter / Period Controls Bar ──────────────────────────────────── */}
      <div className="card shadow-sm border-0 mb-4 bg-light">
        <div className="card-body py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <FaCalendarAlt className="text-primary" />
            <label className="form-label mb-0 fw-semibold text-secondary">
              Comparison Horizon:
            </label>
            <div className="btn-group" role="group" aria-label="Comparison Horizon">
              {[2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`btn btn-sm ${
                    numYears === n ? "btn-primary fw-bold" : "btn-outline-primary"
                  }`}
                  onClick={() => handleYearsChange(n)}
                >
                  {n} Years
                </button>
              ))}
            </div>
          </div>

          <div className="text-muted small">
            {reportData?.years && reportData.years.length > 0 && (
              <span>
                Comparing Fiscal Years: <strong>{reportData.years.join(" vs ")}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Content Area ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="card shadow-sm p-5 text-center my-4">
          <div className="spinner-border text-primary mx-auto mb-3" role="status" />
          <h5 className="text-secondary fw-semibold">Loading Balance Sheet Comparison…</h5>
          <p className="text-muted small mb-0">Computing multi-year cumulative balances &amp; equity matrix</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={() => fetchReport(numYears)}>
            Retry
          </button>
        </div>
      ) : !reportData || !reportData.years || reportData.years.length === 0 ? (
        <div className="alert alert-warning">No financial comparison data available.</div>
      ) : (
        <BalanceSheetComparisonTable reportData={reportData} />
      )}
    </div>
  );
};

const FaBalanceScaleHeader = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 640 512"
    height="1.5em"
    width="1.5em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M256 320h128c17.67 0 32-14.33 32-32s-14.33-32-32-32H256c-17.67 0-32 14.33-32 32s14.33 32 32 32zm376.62-111.45l-112-160a31.998 31.998 0 0 0-52.61.01l-112 160c-18.73 26.76 1.48 63.44 34.3 63.44h41.69V416h-48V160h32c17.67 0 32-14.33 32-32s-14.33-32-32-32H288c-17.67 0-32 14.33-32 32s14.33 32 32 32h32v256h-48V272h41.69c32.82 0 53.04-36.68 34.3-63.45l-112-160a31.998 31.998 0 0 0-52.61.01l-112 160C-3.35 235.32 16.86 272 49.69 272H96v176c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32V272h46.31c32.83 0 53.04-36.68 34.31-63.45z"></path>
  </svg>
);

export default BalanceSheetComparisonPage;
